import { ReactElement } from 'react';

import { StepStage } from '@philips-emr/tws-constants';

export interface Step {
  current: boolean;
  done: boolean;
  enabled: boolean;
  identifier: string;
  last: boolean;
}

export interface StepOptions {
  enabled: boolean;
  done?: boolean;
  collapsed: boolean;
  result?: ReactElement | null;
}

export interface StepAction {
  identifier: string;
  steps: Record<string, Step>;
}

function resetStep(step: Step): Step {
  return createStep(step, {
    collapsed: true,
    done: false,
    enabled: false,
    result: null,
  });
}

function createStep(step: Step, options: StepOptions): Step {
  return { ...step, ...options };
}

function getStepIdentifiers(steps: Record<string, Step>): string[] {
  return Object.keys(steps);
}

function updateStepStages(identifier: string, steps: Record<string, Step>): Record<string, Step> {
  const identifiers = getStepIdentifiers(steps);

  const currentIndex = identifiers.findIndex(id => id === identifier);

  const prevIndex = currentIndex <= 0 ? -1 : currentIndex - 1;
  const nextIndex = currentIndex >= identifiers.length - 1 ? -1 : currentIndex + 1;

  const stages = {
    [currentIndex]: StepStage.CURRENT,
    [prevIndex]: StepStage.PREV,
    [nextIndex]: StepStage.NEXT,
  };

  return identifiers.reduce((accumulator, identifier, index) => {
    const step = steps[identifier];
    const stage = stages[index] || StepStage.NONE;

    return {
      ...accumulator,
      [identifier]: { ...step, stage },
    };
  }, {});
}

function editStep(previousIdentifier: string, editIdentifier: string, steps: Record<string, Step>): StepAction {
  const identifiers = getStepIdentifiers(steps);

  const editIndex = identifiers.findIndex((identifier) => editIdentifier === identifier);
  let currentIndex = identifiers.findIndex((identifier) => previousIdentifier === identifier);

  const updatedSteps = { ...steps };

  while (currentIndex > editIndex) {
    const identifier = identifiers[currentIndex];

    updatedSteps[identifier] = resetStep(updatedSteps[identifier]);

    currentIndex--;
  }

  const step = updatedSteps[editIdentifier];

  updatedSteps[editIdentifier] = createStep(step, {
    collapsed: false,
    done: true,
    enabled: true,
  });

  return {
    identifier: editIdentifier,
    steps: updatedSteps,
  };
}

function submitStep(identifier: string, steps: Record<string, Step>, getStepResult: (identifier: string) => ReactElement | null): StepAction {
  const identifiers = getStepIdentifiers(steps);

  const index = identifiers.findIndex((key) => identifier === key);

  const updatedSteps = { ...steps };

  const result = getStepResult(identifier);

  const step = updatedSteps[identifier];
  updatedSteps[identifier] = createStep(step, {
    collapsed: true,
    done: true,
    enabled: true,
    result,
  });

  const nextIndex = index + 1;
  const nextIdentifier = identifiers[nextIndex];

  if (!nextIdentifier) {
    return { identifier, steps: updatedSteps };
  }

  const nextStep = updatedSteps[nextIdentifier];

  updatedSteps[nextIdentifier] = createStep(nextStep, {
    collapsed: false,
    enabled: true,
  });

  return {
    identifier: nextIdentifier,
    steps: updatedSteps,
  };
}

export { createStep, editStep, getStepIdentifiers, resetStep, submitStep, updateStepStages };
